import type { KundaliLang } from "./KundaliFormCard.i18n";
import { isValidIndianPhone, isValidOptionalEmail } from "@/lib/phone-india";

export type KundaliFieldErrors = Partial<
  Record<"name" | "birthDate" | "birthTime" | "birthPlace" | "phone" | "email", string>
>;

type ValidationInput = {
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  language: KundaliLang;
  name: string;
  phone: string;
  email: string;
  placeSelected: boolean;
  requireBirthTime: boolean;
  requireContact: boolean;
};

type ValidationResult =
  | { errors: KundaliFieldErrors; ok: false; firstField: keyof KundaliFieldErrors }
  | { birthDateIso: string; errors: KundaliFieldErrors; ok: true };

/**
 * Whether the primary submit CTA should be enabled w.r.t. the contact gate.
 * Only blocks on phone when the surface requires contact (chat-entry); the
 * single submit button stays the one decision (rule 39), it's just disabled
 * until a valid phone is present — no separate "submit contact" button.
 */
export function isContactGateSatisfied(phone: string, requireContact: boolean): boolean {
  if (!requireContact) return true;
  return isValidIndianPhone(phone);
}

export function normalizeBirthDateInput(input: string): string | null {
  const raw = input.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) return validIsoDate(isoMatch[1], isoMatch[2], isoMatch[3]);

  const displayMatch = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw);
  if (displayMatch) return validIsoDate(displayMatch[3], displayMatch[2], displayMatch[1]);

  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  if (compactMatch) return validIsoDate(compactMatch[3], compactMatch[2], compactMatch[1]);

  return null;
}

export function formatBirthDateDisplayInput(input: string): string {
  const raw = input.trim();
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (isoMatch) {
    return [isoMatch[3].padStart(2, "0"), isoMatch[2].padStart(2, "0"), isoMatch[1]].join("/");
  }

  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function validateKundaliForm(input: ValidationInput): ValidationResult {
  const errors: KundaliFieldErrors = {};
  if (!input.name.trim()) {
    errors.name = localized(input.language, "Please enter your name.", "Kripya apna naam likhein.");
  }

  const birthDateIso = normalizeBirthDateInput(input.birthDate);
  if (hasInvalidBirthDate(birthDateIso)) {
    errors.birthDate = localized(
      input.language,
      "Enter birth date as dd/mm/yyyy.",
      "Janm tithi dd/mm/yyyy format mein likhein."
    );
  }

  if (isMissingRequiredBirthTime(input)) {
    errors.birthTime = localized(
      input.language,
      "Enter birth time to start the chat.",
      "Chat ke liye janm samay zaroori hai."
    );
  }

  if (isMissingBirthPlace(input)) {
    errors.birthPlace = localized(
      input.language,
      "Please pick your birth city from the dropdown.",
      "Kripya dropdown se apna janm sthal chunein."
    );
  }

  if (hasInvalidPhone(input)) {
    errors.phone = localized(
      input.language,
      "Enter your 10-digit mobile number.",
      "Apna 10-digit mobile number daalein."
    );
  }

  if (input.email.trim() && !isValidOptionalEmail(input.email)) {
    errors.email = localized(
      input.language,
      "Enter a valid email, or leave it blank.",
      "Sahi email daalein, ya khaali chhod dein."
    );
  }

  const firstField = firstErrorField(errors);
  if (firstField) return { errors, firstField, ok: false };
  return { birthDateIso: birthDateIso as string, errors, ok: true };
}

function firstErrorField(errors: KundaliFieldErrors): keyof KundaliFieldErrors | null {
  if (errors.name) return "name";
  if (errors.phone) return "phone";
  if (errors.email) return "email";
  if (errors.birthDate) return "birthDate";
  if (errors.birthTime) return "birthTime";
  if (errors.birthPlace) return "birthPlace";
  return null;
}

// Phone is required only on chat-entry surfaces (requireContact). When required,
// the value must normalize to a valid Indian mobile. When optional, a non-empty
// value is still validated (so a typo is caught), but blank is allowed.
function hasInvalidPhone(input: ValidationInput): boolean {
  const hasValue = Boolean(input.phone.trim());
  if (input.requireContact) return !isValidIndianPhone(input.phone);
  return hasValue && !isValidIndianPhone(input.phone);
}

function localized(language: KundaliLang, en: string, hi: string) {
  return language === "hi" ? hi : en;
}

function hasInvalidBirthDate(birthDateIso: string | null) {
  return !birthDateIso || !isAllowedBirthDate(birthDateIso);
}

function isMissingRequiredBirthTime(input: ValidationInput) {
  return input.requireBirthTime && !input.birthTime.trim();
}

function isMissingBirthPlace(input: ValidationInput) {
  return !input.birthPlace.trim() || !input.placeSelected;
}

function validIsoDate(year: string, month: string, day: string): string | null {
  const yyyy = Number(year);
  const mm = Number(month);
  const dd = Number(day);
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (date.getUTCFullYear() !== yyyy || date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) {
    return null;
  }
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function isAllowedBirthDate(isoDate: string) {
  return isoDate >= "1900-01-01" && isoDate <= new Date().toISOString().slice(0, 10);
}
